import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/*
 * Runbook-ul aplicatiei - o solutie din IT Solutions (`BusinessAppsRunbookController`).
 *
 *   GET  /business-apps/{id}/runbook                                  solutia legata, cu pasii pe niveluri
 *   POST /business-apps/{id}/runbook                                  steps[i][level|text], base -> pasii scrisi IN solutie
 *   POST /business-apps/{id}/runbook/create                           solutie noua din sablon, legata
 *   POST /business-apps/{id}/runbook/link                             solution_id (0 = scoate legatura)
 *   GET  /business-apps/{id}/runbook/solutions?q=                     solutiile firmei, pentru alegere
 *   GET  /easeedesk/tickets/{id}/business-apps/runbooks               runbook-urile aplicatiilor tichetului
 *   POST /easeedesk/tickets/{id}/business-apps/runbooks/{appId}/done  steps[], base -> nota interna
 */

export type RunbookStep = { n: number; level: number; text: string; levelLabel?: string }

export type RunbookIntro = { level: number; text: string }

export type Runbook = {
  solutionId: number
  title: string
  category: string
  /** Solutia legata a fost stearsa / stinsa in IT Solutions. */
  missing: boolean
  /** Categoria solutiei e restransa pe roluri, iar rolul omului nu o vede. */
  restricted: boolean
  steps: RunbookStep[]
  intros: RunbookIntro[]
  /** Amprenta nivelurilor citite - se trimite inapoi la scriere. */
  base: string
  linkedBy: string
  linkedAt: string
  convertedFrom: number | null
}

export type AppRunbookPage = {
  ready: boolean
  message?: string
  appId?: number
  runbook?: Runbook | null
  canEdit?: boolean
  canLink?: boolean
  category?: string
  levels?: { value: number; label: string }[]
  libraryUrl?: string
}

export type TicketRunbook = Runbook & { appId: number; appName: string }

export type TicketRunbooksPage = { ready: boolean; ticketId?: number; runbooks: TicketRunbook[]; canMark: boolean }

export type RunbookSolution = { id: number; title: string; category: string }

export type RunbookWritten = { status: 'OK' | 'ERROR'; message: string; solutionId?: number; note?: string }

export function appRunbookQuery(appId: number) {
  return queryOptions({
    queryKey: ['business-apps', 'runbook', appId],
    queryFn: () => apiGet<AppRunbookPage>(`/business-apps/${appId}/runbook`),
    enabled: appId > 0,
  })
}

export function runbookSolutions(appId: number, q: string) {
  return apiGet<{ ready: boolean; solutions: RunbookSolution[] }>(`/business-apps/${appId}/runbook/solutions?q=${encodeURIComponent(q)}`)
}

export function createRunbook(appId: number) {
  return apiPost<RunbookWritten>(`/business-apps/${appId}/runbook/create`, {})
}

export function linkRunbook(appId: number, solutionId: number) {
  return apiPost<RunbookWritten>(`/business-apps/${appId}/runbook/link`, { solution_id: solutionId })
}

export function saveRunbook(appId: number, fields: { steps: { level: number; text: string }[]; base: string }) {
  return apiPost<RunbookWritten>(`/business-apps/${appId}/runbook`, fields as unknown as Record<string, PostValue>)
}

export function ticketRunbooksQuery(ticketId: number) {
  return queryOptions({
    queryKey: ['business-apps', 'runbook', 'ticket', ticketId],
    queryFn: () => apiGet<TicketRunbooksPage>(`/easeedesk/tickets/${ticketId}/business-apps/runbooks`),
    enabled: ticketId > 0,
  })
}

export function markRunbookSteps(ticketId: number, appId: number, steps: number[], base: string) {
  return apiPost<RunbookWritten>(`/easeedesk/tickets/${ticketId}/business-apps/runbooks/${appId}/done`, { steps, base })
}
